import {Component, EventEmitter, Input, OnInit, Optional, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {IPerson} from '../../model/person';
import {ApplicationConstants} from '../../constants/application.constants';
import {AutoUnsubscribe} from '../../../../infrastructure/decorators/auto-unsubscribe.decorator';
import {AzureAuthService} from '../../../../infrastructure/services/azure-auth.service';
import {ModalService} from '../../service/modal/modal.service';
import {HttpParams} from '@angular/common/http';

export interface FilterConfig {
  from?: Date | null;
  to?: Date | null;
  email?: string | null;
}

export interface FilterResult {
  from?: Date | null;
  to?: Date | null;
  email?: string | null;
  name?: string | null;
}

@AutoUnsubscribe()
@Component({
    selector: 'filter-modal',
    templateUrl: './filter-modal.component.html',
    styleUrls: ['./filter-modal.component.scss'],
    standalone: false
})
export class FilterModalComponent implements OnInit {
  @Input() config!: FilterConfig;
  @Output() applyFilter: EventEmitter<FilterResult> =
    new EventEmitter<FilterResult>();
  selectedPersonEmail: string = '';
  selectedPersonFullName: string = '';
  public isLoggedInUserDirector: boolean = false;
  private appConstants: ApplicationConstants = new ApplicationConstants();
  personList: IPerson[] = [];

  constructor(
    @Optional() public dialogRef: MatDialogRef<FilterModalComponent>,
    private azureAuthService: AzureAuthService,
    private modalService: ModalService
  ) {}

  form: FormGroup = new FormGroup({
    from: new FormControl(null, [Validators.required]),
    to: new FormControl(null, [Validators.required]),
    email: new FormControl<IPerson | null>(null, [Validators.required])
  });

  handleClose() {
    if (!this.dialogRef) {
      return;
    }
    this.dialogRef.close(null);
  }

  ngOnInit() {
    this.isLoggedInUserDirector = this.azureAuthService.checkUserAzureTokenHasDirectorRole();
    if (this.isLoggedInUserDirector) {
      this.loadPersonList();
    } else {
      const {from, to} = this.config ?? {};
      this.azureAuthService.loggedInUserEmail.subscribe(x => {
        this.selectedPersonEmail = x;
        this.form.patchValue({
          from,
          to,
          x
        });
      });
      this.azureAuthService.loggedInUserName.subscribe(x => {
        this.selectedPersonFullName = x;
      });
      this.form.get('email')?.setValidators(null);
    }
  }

  loadPersonList() {
    const params = new HttpParams()
      .set('All', 'false')
      .set('PropertyName', 'FirstName')
      .set('PageSize', '1000');

    this.modalService.getHsPersonData(params).subscribe({
      next: (response) => {
        const allUserItem: IPerson = {
          personId: 0,
          firstName: 'All',
          lastName: 'User',
          email: this.appConstants.AllUser
        };
        
        if (response && response.data && Array.isArray(response.data)) {
          this.personList = [allUserItem, ...response.data];
        } else if (response && Array.isArray(response)) {
          this.personList = [allUserItem, ...response];
        } else if (response && response.items && Array.isArray(response.items)) {
          this.personList = [allUserItem, ...response.items];
        } else {
          this.personList = [allUserItem];
        }
        
        this.form.controls['email'].setValue(allUserItem);
        this.selectedPersonEmail = this.appConstants.AllUser;
        this.selectedPersonFullName = 'All User';
        
        this.getHsPersonListData();
      },
      error: (error) => {
        console.error('API Error:', error);
        const allUserItem: IPerson = {
          personId: 0,
          firstName: 'All',
          lastName: 'User',
          email: this.appConstants.AllUser
        };
        this.personList = [allUserItem];
        this.form.controls['email'].setValue(allUserItem);
        this.selectedPersonEmail = this.appConstants.AllUser;
        this.selectedPersonFullName = 'All User';
        
        this.getHsPersonListData();
      }
    });
  }

  patchFilterForm() {
    const {from, to} = this.config ?? {};
    this.form.patchValue({
      from,
      to
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this.patchFilterForm();
    }
  }
  apply() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    else if(this.isLoggedInUserDirector && typeof this.personEmailControl.value === 'object' &&
            this.personEmailControl.value?.email !== this.appConstants.AllUser && 
            !this.selectedPersonEmail && 
            !this.isValidEmail(this.personEmailControl.value.email)){
      this.form.controls['email'].markAsTouched();
      return;
    }

    let {from, to, email} = this.form.value;
    const name = this.selectedPersonFullName;
    
    if (typeof email === 'object' && email !== null) {
      if (email.email === this.appConstants.AllUser) {
        email = this.appConstants.AllUser;
      } else {
        email = email.email || email;
      }
    } else {
      email = this.selectedPersonEmail || email;
    }
    
    const result = {from, to, email, name};
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }

    this.applyFilter.emit(result);
  }

  get fromDateControl() {
    return this.form.get('from') as FormControl;
  }

  get toDateControl() {
    return this.form.get('to') as FormControl;
  }

  get personEmailControl() {
    return this.form.get('email') as FormControl;
  }
  getHsPersonListData() {
    if (this.config && this.config.email && this.config.email !== this.appConstants.AllUser) {
      this.selectedPersonEmail = this.config.email;
      const matchingPerson = this.personList.find(p => p.email === this.config.email);
      if (matchingPerson) {
        this.form.controls['email'].setValue(matchingPerson);
        this.selectedPersonFullName = matchingPerson.firstName + ' ' + matchingPerson.lastName;
      }
    }
    
    this.patchFilterForm();
  }

  isValidEmail(searchedUserEmail: string): boolean {
    const regexp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regexp.test(searchedUserEmail);
  }
  onPersonSelection(selectedPerson: IPerson) {
    if (selectedPerson && selectedPerson.email) {
      this.selectedPersonEmail = selectedPerson.email;
      if (selectedPerson.firstName === 'All' && selectedPerson.lastName === 'User') {
        this.selectedPersonFullName = 'All User';
      } else {
        this.selectedPersonFullName = selectedPerson.firstName + ' ' + selectedPerson.lastName;
      }
    }
  }
}
